import { track } from '@src/services/analytics';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { formatCents } from '@src/common';
import { device } from '@src/common/breakpoints';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { useWidgetsTheme as theme } from '@src/core/theme';
import { completeCartSelector } from '@src/features/cart/store/cart';
import { useCoupon, CartCouponArea } from '@src/features/coupons';
import { BodyLargeBold, BodyRegular, BodyRegularMedium, Heading5 } from '@src/fonts/typography/Typography';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import useTranslate, { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import GradientButton from '@src/components/reusable/button/GradientButton';
import { ServiceFeeDisplay } from '@src/components/reusable/ServiceFee';
import ErrorWarning from './ErrorWarning';
import { AlertContainer, AlertContent } from '@src/components/reusable/Alert/style';
import { snakeCase } from 'lodash';
import { getCartServiceFeeDescriptions } from '@src/domains/cart';
import { hasCouponEligibleItem } from '@src/domains/coupons';

type CartCheckoutProps = {
  enableCoupon?: boolean;
  onCheckout?: () => void;
  proceedWithErrors?: boolean;
};

const CartCheckout = (props: CartCheckoutProps) => {
  const { enableCoupon = true, onCheckout, proceedWithErrors = false } = props;

  const actionColor = theme('actionColor');
  const checkoutPage = useWidgetParam('checkout-page');
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const { cart, hasErrors } = useAppSelector(completeCartSelector);
  const { discount: cartDiscount, canUse } = useCoupon();

  const onCheckoutButtonClick = () => {
    track('cartCheckoutClick');

    if (onCheckout) {
      onCheckout();
    } else {
      dispatch(changeLocation(checkoutPage ?? ''));
    }
  };

  const fromValue = cartDiscount.fromCents;
  const toValue = cartDiscount.toCents;
  const discount = cartDiscount.percentage;
  const generalErrors = cart?.generalErrors;
  const hasEligibleItem = hasCouponEligibleItem(cart?.items ?? []);

  const serviceFeeDescription = useMemo(() => {
    return getCartServiceFeeDescriptions(cart?.items ?? []).join(', ') || undefined;
  }, [cart]);

  return (
    <CartCheckoutContainer>
      {enableCoupon && canUse && hasEligibleItem ? (
        <>
          <CartCouponArea />

          <HorizontalBar />
        </>
      ) : null}

      {cart?.coupon?.invalid ? (
        <div>
          <ErrorWarning>{i18n('checkout_invalidCoupon')}</ErrorWarning>
        </div>
      ) : null}

      <ServiceFeeDisplay
        serviceFeeCurrencyInfo={cart?.costPreview.serviceFeeCurrencyInfo}
        serviceFeeDescription={serviceFeeDescription}
      />

      {discount ? (
        <CartCheckoutInfo>
          <TotalWithoutDiscountHeader>{i18n('cart_subtotal')}</TotalWithoutDiscountHeader>
          <TotalWithoutDiscountValue>{formatCents(fromValue)}</TotalWithoutDiscountValue>
        </CartCheckoutInfo>
      ) : null}

      <CartCheckoutInfo>
        <TotalHeader>{i18n('cart_totalLabel')}</TotalHeader>
        <TotalValue>{formatCents(toValue)}</TotalValue>
      </CartCheckoutInfo>

      {generalErrors && generalErrors.length ? (
        <AlertContainer severity='error'>
          <AlertContent>
            {generalErrors.map((error) => (
              <span key={error.type}>{i18n(snakeCase(error.type) as unknown as DefaultMessagesKeys)}</span>
            ))}
          </AlertContent>
        </AlertContainer>
      ) : null}

      <ButtonArea>
        <GradientButton
          onClick={onCheckoutButtonClick}
          disabled={!proceedWithErrors && hasErrors}
          actionColor={actionColor}
        >
          {i18n('cart_checkout')}
        </GradientButton>
      </ButtonArea>
    </CartCheckoutContainer>
  );
};

const ButtonArea = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;

  ${device.mobile} {
    margin-bottom: 0px;
  }
`;

const TotalHeader = styled.p`
  ${BodyLargeBold}
  ${DarkTextColor}

  ${device.mobile} {
    ${Heading5}
  }
`;
const TotalValue = styled.p`
  ${BodyLargeBold}
  ${DarkTextColor}

  ${device.mobile} {
    ${Heading5}
  }
`;
const HorizontalBar = styled.hr`
  border-top: 1px solid #cdcdcd;
  margin-top: 0px;
  margin-bottom: 16px;
  width: 100%;
`;
const CartCheckoutInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
`;
const CartCheckoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TotalWithoutDiscountHeader = styled.p`
  ${BodyRegular}
`;

const TotalWithoutDiscountValue = styled.p`
  ${BodyRegularMedium}
  border-radius: 4px;
  background: #eee;
  padding: 2px 6px;
  text-decoration: line-through;
`;

export default CartCheckout;
